import { api } from '@/lib/api.js';

// Referral-partner admin API client (backend: routes/tracon.routes.js
// "Referrals" block). All calls go through the /api/tracon mount so they
// carry the broker JWT — the activity_log actor is the broker, and this
// surface never needs the shared API key the way routes/partner.routes.js
// (the partner-facing portal + nightly cron) does.
//
// The v1 partner portal owned these actions; the v1→v2 swap dropped the
// admin *screen* but not the backend. This restores broker access to it
// inside TRACON v2.

function qs(params = {}) {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v != null && v !== '') u.set(k, v);
  }
  // Cache-bust — the api layer has no SWR for this surface yet.
  u.set('_t', Date.now());
  return u.toString();
}

// GET the full referral list for a partner (status='all' so the returned
// `counts` cover every bucket; the UI filters client-side).
export function getReferrals({ partner, status = 'all', staleAfterDays } = {}) {
  return api.get(
    `/api/tracon/referrals?${qs({ partner, status, stale_after_days: staleAfterDays })}`,
    // Auto-checks fan out to FAA + CRM per row, so give this one room.
    { timeoutMs: 90_000 }
  );
}

// Per-referral decisions. `reason` is optional on qualify, REQUIRED on
// disqualify (server enforces). Backend auto-routes a qualify on an
// already-closed quarter straight to pending_payout.
export function qualifyReferral(partnerRefId, reason) {
  const body = reason && reason.trim() ? { reason: reason.trim() } : {};
  return api.post(`/api/tracon/referrals/${encodeURIComponent(partnerRefId)}/qualify`, body);
}

export function disqualifyReferral(partnerRefId, reason) {
  return api.post(`/api/tracon/referrals/${encodeURIComponent(partnerRefId)}/disqualify`, {
    reason: (reason || '').trim(),
  });
}

export function reopenReferral(partnerRefId) {
  return api.post(`/api/tracon/referrals/${encodeURIComponent(partnerRefId)}/reopen`, {});
}

// Quarter-level payout: settle one closed quarter for a partner. Sweeps every
// accrued-but-unpaid referral (qualified OR pending_payout) in that quarter to
// 'paid'. Idempotent — re-running pays 0.
export function payQuarter(slug, quarter, paidAt) {
  return api.post(`/api/tracon/referrals/${encodeURIComponent(slug)}/pay-quarter`, {
    quarter,
    paid_at: paidAt,
  });
}

// Escape hatch for a misclicked payout: flip a quarter's paid rows back to
// pending_payout and clear the paid date.
export function reopenQuarter(slug, quarter) {
  return api.post(`/api/tracon/referrals/${encodeURIComponent(slug)}/reopen-quarter`, {
    quarter,
  });
}
